import utils from './utils';
import {
  COLLAPSED,
  COLLAPSED_AS_ROUNDED,
  COLLAPSED_WITH_MESSAGE_NOTIFICATION,
  COLLAPSED_WITH_MESSAGE_NOTIFICATION_RESPONSIVE,
  EXPANDED,
  EXPANDED_WITH_MESSAGE_NOTIFICATION,
} from '../constants/widget-size';
import { MEMBERS_APP_ID } from '../constants/apps-ids';

declare const window: any;

export default {
  navigateToSection(sectionIdentifier, state, onFailure) {
    return window.Wix.Utils.navigateToSection(
      sectionIdentifier,
      state,
      onFailure,
    );
  },
  getLocale() {
    return window.Wix.Utils.getLocale();
  },

  getCompId() {
    return window.Wix.Utils.getCompId();
  },

  getDeviceType() {
    return window.Wix.Utils.getDeviceType();
  },

  isMobileDevice() {
    return this.getDeviceType() === 'mobile';
  },

  colorationDecision({ userChoice, addPanelColor, defaultColor, color1 }) {
    if (userChoice) {
      return userChoice;
    }
    const color = addPanelColor || defaultColor;
    if (color) {
      if (utils.isWhite(color)) {
        return color1;
      }
      return color;
    }
    return '';
  },

  getColorByParam(param) {
    return window.Wix.Styles.getStyleColorByKey(param);
  },

  getStyleParams(): Promise<{ fonts: any; colors: any; booleans: any }> {
    return new Promise((resolve) => {
      window.Wix.Styles.getStyleParams(resolve);
    });
  },

  async isResponsive() {
    const { booleans } = await this.getStyleParams();
    return !!booleans?.responsive;
  },

  async isResponsiveMobile() {
    return (await this.isResponsive()) && this.isMobileDevice();
  },

  getSiteColors() {
    return new Promise((resolve) => {
      window.Wix.Styles.getSiteColors(resolve);
    });
  },
  currentMember() {
    return new Promise((resolve) => {
      window.Wix.currentMember((member) =>
        resolve({
          attributes: { privacyStatus: member?.attributes?.privacyStatus },
          status: member?.status,
        }),
      );
    });
  },

  async isPublicMember() {
    const member = (await this.currentMember()) as any;
    return !!member && member.attributes.privacyStatus === 'PUBLIC';
  },

  async isBlockedMember() {
    const member = (await this.currentMember()) as any;
    return !!member && member.status === 'DENIED';
  },

  resizeWindow(width, height) {
    return new Promise((resolve) => {
      window.Wix.resizeWindow(width, height, resolve);
    });
  },

  async resizeToCollapsedWithMessageNotification() {
    return (await this.isResponsiveMobile())
      ? this.resizeWindow(
          COLLAPSED_WITH_MESSAGE_NOTIFICATION_RESPONSIVE.WIDTH,
          COLLAPSED_WITH_MESSAGE_NOTIFICATION_RESPONSIVE.HEIGHT,
        )
      : this.resizeWindow(
          COLLAPSED_WITH_MESSAGE_NOTIFICATION.WIDTH,
          COLLAPSED_WITH_MESSAGE_NOTIFICATION.HEIGHT,
        );
  },

  resizeToExpandedWithMessageNotification() {
    return this.resizeWindow(
      EXPANDED_WITH_MESSAGE_NOTIFICATION.WIDTH,
      EXPANDED_WITH_MESSAGE_NOTIFICATION.HEIGHT,
    );
  },

  resizeToCollapsedMode(asRounded) {
    return asRounded
      ? this.resizeWindow(
          COLLAPSED_AS_ROUNDED.WIDTH,
          COLLAPSED_AS_ROUNDED.HEIGHT,
        )
      : this.resizeWindow(COLLAPSED.WIDTH, COLLAPSED.HEIGHT);
  },

  resizeToExpandedMode() {
    return this.resizeWindow(EXPANDED.WIDTH, EXPANDED.HEIGHT);
  },

  async resizeToExpandedModeMobileResponsive() {
    await this.resizeWindow('100%', '100%');
    window.Wix.Mobile.showFullscreen();
  },

  resizeToHidden() {
    return this.resizeWindow(0, 0);
  },

  getPublicData(setting, onPublicDataChanged, setDefaultSettings?) {
    return new Promise((resolve) => {
      window.Wix.Data.Public.get(
        setting,
        { scope: 'APP' },
        (res) => {
          onPublicDataChanged(res);
          resolve();
        },
        () => {
          setDefaultSettings && setDefaultSettings(setting);
          resolve();
        },
      );
    });
  },

  setDataPublic(tab, data) {
    return new Promise((resolve, reject) => {
      window.Wix.Data.Public.set(tab, data, { scope: 'APP' }, resolve, reject);
    });
  },

  navigateToMemberProfile(memberId) {
    window.Wix.getPublicAPI(
      { appDefinitionId: MEMBERS_APP_ID },
      (api) => {
        api
          .navigateToMember({
            memberId,
          })
          .catch(console.error);
      },
      console.error,
    );
  },

  getResizedImgUrl(relativeUrl, width, height) {
    return window.Wix.Utils.Media.getResizedImageUrl(
      relativeUrl,
      width,
      height,
    );
  },

  getViewMode() {
    return window.Wix.Utils.getViewMode();
  },

  isInTemplate() {
    return window.Wix.Utils.getDemoMode();
  },

  getMetaSiteId() {
    return this.getInstanceValue('metaSiteId');
  },

  getUserSessionToken() {
    return new Promise((resolve) => {
      window.Wix.Activities.getUserSessionToken(resolve);
    });
  },

  getInstanceId() {
    return window.Wix.Utils.getInstanceId();
  },

  getInstanceValue(name: string) {
    return window.Wix.Utils.getInstanceValue(name);
  },

  syncPageTitle(handler) {
    window.Wix.Settings.getSiteInfo((siteInfo) => {
      handler(siteInfo.pageTitle);
    });
  },

  getInstance() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('instance') || '';
  },

  openModal(url, width, height) {
    window.Wix.openModal(url, width, height, undefined, window.Wix.Theme.BARE);
  },

  closeModal() {
    window.Wix.closeWindow();
  },

  subscribeForStartPrivateChat(handler) {
    window.Wix.PubSub.subscribe('ChatWidget.startPrivateChat', handler, false);
  },

  subscribeToStartChannelRequest(handler) {
    window.Wix.PubSub.subscribe('ChatWidget.startChannel', handler, false);
  },

  publishToPubSub(event, data) {
    window.Wix.PubSub.publish(event, data, true);
  },

  subscribeForComponentDeleted(handler) {
    window.Wix.addEventListener(window.Wix.Events.COMPONENT_DELETED, handler);
  },

  subscribeForPublicDataChanged(handler) {
    window.Wix.addEventListener(window.Wix.Events.PUBLIC_DATA_CHANGED, handler);
  },

  subscribeForSettingsUpdated(handler) {
    window.Wix.addEventListener(window.Wix.Events.SETTINGS_UPDATED, handler);
  },

  subscribeForEditModeChanged(handler) {
    window.Wix.addEventListener(window.Wix.Events.EDIT_MODE_CHANGE, handler);
  },

  subscribeForThemeChanged(handler) {
    window.Wix.addEventListener(window.Wix.Events.THEME_CHANGE, handler);
  },

  subscribeForNavigation(handler) {
    window.Wix.addEventListener(window.Wix.Events.PAGE_NAVIGATION, () => {
      this.syncPageTitle(handler);
    });
  },

  subscribeForMetadataChange(handler) {
    window.Wix.addEventListener(window.Wix.Events.SITE_METADATA_CHANGED, () => {
      this.syncPageTitle(handler);
    });
  },

  subscribeForSettingsClosed(handler) {
    window.Wix.addEventListener(window.Wix.Events.EDITOR_EVENT, handler);
  },

  subscribeToExpandWidgetRequest(handler) {
    window.Wix.PubSub.subscribe('ChatWidget.expandWidget', handler, true);
  },

  subscribeToCollapseWidgetRequest(handler) {
    window.Wix.PubSub.subscribe('ChatWidget.collapseWidget', handler, false);
  },

  subscribeToGetChatSettingsRequest(handler) {
    window.Wix.PubSub.subscribe('ChatWidget.getChatSettings', handler, false);
  },

  subscribeToGetChatStateRequest(handler) {
    window.Wix.PubSub.subscribe('ChatWidget.getChatState', handler, false);
  },

  subscribeToSendMessageRequest(handler) {
    window.Wix.PubSub.subscribe('ChatWidget.sendMessage', handler, false);
  },

  subscribeToFocusChannelRequest(handler) {
    window.Wix.PubSub.subscribe('ChatWidget.focusChannel', handler, false);
  },

  subscribeToGetChannelRequest(handler) {
    window.Wix.PubSub.subscribe('ChatWidget.getChannel', handler, false);
  },

  subscribeToPageNavigation(handler) {
    window.Wix.addEventListener(window.Wix.Events.PAGE_NAVIGATION, handler);
  },

  getCurrentPageId(handler) {
    window.Wix.getCurrentPageId(handler);
  },

  updateQab(data: {
    visible?: boolean;
    notifications?: boolean;
    color?: string;
  }) {
    window.Wix.Mobile.setMobileActionBarButton(data);
  },

  getBiToken() {
    return this.getInstanceValue('biToken');
  },

  getAid() {
    return this.getInstanceValue('aid');
  },

  getUid() {
    return this.getInstanceValue('uid');
  },

  registerQab(callback) {
    window.Wix.addEventListener(
      window.Wix.Events.QUICK_ACTION_TRIGGERED,
      callback,
    );
  },

  showMobileComponent() {
    window.Wix.Mobile.showFullscreen();
  },

  hideMobileComponent() {
    window.Wix.Mobile.hideFullscreen();
  },

  onReady(callback: Function) {
    window.Wix.onReady(callback);
  },

  reportApplicationLoaded() {
    window.Wix.Performance.applicationLoaded();
  },

  trackEvent(event: string, param: any) {
    try {
      window.Wix.Analytics.trackEvent(event, param);
    } catch (e) {
      //swallow. This method works only in `site` mode and crashes on any other mode.
    }
  },
};
